import type {SharedData} from '@gaia/types';
import fetchJSON from '@gaia/utils/fetchJSON';
import {registerSavedArea} from '@gaia/utils/gaia/saveArea';
import {boldTrackColors} from '@gaia/utils/gaia/trackColors';
import turfCleanCoords from '@turf/clean-coords';
import turfRewind from '@turf/rewind';
import find from 'lodash/find';
import keyBy from 'lodash/keyBy';
import mapValues from 'lodash/mapValues';
import uniqueId from 'lodash/uniqueId';

export default function takeLocal(
    action: TakeAreaLocalAction,
    areas: {[areaLocalId: string]: Area},
    setAreas: React.Dispatch<React.SetStateAction<{[areaLocalId: string]: Area}>>,
    sharedData: SharedData
) {
    const area = action.area;
    const {id: areaId} = area.properties;
    const areaLocalId = area.properties.localId;

    if (areaLocalId && areas[areaLocalId]) {
        if (action.callback) action.callback(areas[areaLocalId]);
        return [areaLocalId];
    }

    if (areaId && areas[areaId]) {
        if (action.callback) action.callback(areas[areaId]);
        return [areaId];
    }

    if (!areaId) throw new Error('Cannot call takeLocal on an area without an id');

    const initialArea: Area = {
        ...area,
        isGeometryLoaded: false,
        properties: {
            localId: areaId,
            archived: false,
            color: boldTrackColors[0],
            title: '',
            notes: '',
            type: 'area',
            isLocallyCreated: false,

            // We encode "null" as a string in vector tiles
            ...(mapValues(area.properties, (value) =>
                value === 'null' ? null : value
            ) as RemoteArea['properties']),
        },
    };

    registerSavedArea(initialArea);

    if (!areas[areaId]) {
        setAreas((latestAreas) => ({
            ...latestAreas,
            [areaId]: initialArea,
        }));
    }

    const pubLink: string =
        sharedData &&
        (sharedData.type === 'area' || sharedData.type === 'folder') &&
        sharedData.pubLink
            ? `?pubLink=${sharedData.pubLink}`
            : '';

    fetchJSON(`/api/objects/area/${area.properties.id}/${pubLink}`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: {'Content-Type': 'application/json'},
    })
        .then((collection) => {
            const localArea = formatRemoteArea(collection, areaId);

            setAreas((latestAreas) => {
                const latestArea: Area = {
                    ...localArea,
                    properties: {
                        ...initialArea.properties,
                        ...localArea.properties,
                    },
                };
                registerSavedArea(latestArea);
                if (action.callback) action.callback(latestArea);

                return {
                    ...latestAreas,
                    [areaId]: latestArea,
                };
            });
        })
        .catch(console.warn);

    return [areaId];
}

export function formatRemoteArea(
    collection: {type: 'FeatureCollection'; features: RemoteArea[]},
    localId: string
): SimpleArea | ComplexArea {
    const area = find(collection.features, (feature: any) => feature.geometry.type === 'Polygon');
    if (!area) throw new Error('Area not found');

    // Only Polygons with a single ring are editable
    const isGeometryEditable = area.geometry.coordinates.length === 1;
    let points: AreaPoint[] = [];

    if (isGeometryEditable) {
        const cleanPolygon = turfRewind(turfCleanCoords(area.geometry));

        // Remove the last coordinate because it is a duplicate of the first
        cleanPolygon.coordinates[0].pop();

        points = cleanPolygon.coordinates[0]
            .map((coord: GeoJSON.Position, order: number) => {
                const point: AreaPoint = {
                    type: 'Feature',
                    properties: {
                        order,
                        localId: uniqueId('areaPoint'),
                        areaLocalId: localId,
                        isLocallyCreated: true,
                        archived: false,
                        title: '',
                        type: 'waypoint',
                    },
                    geometry: {
                        type: 'Point',
                        coordinates: coord.slice(0, 3),
                    },
                };
                return point;
            })
            .sort((p1: AreaPoint, p2: AreaPoint) => p1.properties.order - p2.properties.order);
    }

    return {
        properties: {
            localId,
            archived: false,
            type: 'area',
            isLocallyCreated: false,
            ...area.properties,
        },
        isGeometryLoaded: true,
        ...getAreaGeometry(isGeometryEditable, points, area.geometry),
    };
}

// This function helps enforces types on formatRemoteArea
function getAreaGeometry(
    isGeometryEditable: boolean,
    points: AreaPoint[],
    geometry: RemoteArea['geometry']
):
    | {
          isGeometryEditable: true;
          points: {[id: string]: AreaPoint};
      }
    | {
          isGeometryEditable: false;
          geometry: GeoJSON.Polygon;
      } {
    if (isGeometryEditable) {
        return {
            isGeometryEditable: true,
            points: keyBy(points, 'properties.localId'),
        };
    } else {
        return {
            isGeometryEditable: false,
            geometry,
        };
    }
}
